import {defineStore} from 'pinia'
import {ref} from 'vue'
import serialNumberApiClient from '@/axios/serialNumberApi'
import itemRepoApiClient from '@/axios/itemRepoApi'
import {postRewardCampaignData} from '@/axios/rewardCampaignApi'
import {useI18n} from 'vue-i18n'

export const useGlobalStore = defineStore('global', () => {
  const { t } = useI18n()
  const activeStep = ref(1)
  const minStep = ref(1)
  const maxStep = ref(4)
  const stepOne = ref({
    products: [],
    files: [],
    validStep: false,
    hideSerialNumberErrorAlert: true,
    dealerNames: parseDealerNames()
  })
  const stepTwo = ref({
    personalData: {
      email: '',
      firstname: '',
      lastname: '',
      phone: ''
    },
    deliveryData: {
      company: '',
      firstname: '',
      lastname: '',
      isOtherDeliveryRecipient: false,
      street: '',
      houseNo: '',
      zip: '',
      city: ''
    }
  })
  const stepThree = ref({
    rewards: [],
    manualRewards: []
  })
  const stepFour = ref({
    isNewsletterChecked: false,
    isConditionsOfParticipationChecked: false,
    isDataPrivacyChecked: false
  })
  const isErrorState = ref(false)
  const detailPageUrl = window.app.rewardcampaign.detailPageUrl
  const termsOfParticipationPageUrl = window.app.rewardcampaign.termsofparticipation
  const privacyPageUrl = window.app.rewardcampaign.dataprivacy
  const frcSitekey = window.app.rewardcampaign.friendlycaptcha_sitekey
  const mailReceiver = window.app.rewardcampaign.mail_receiver
  const ccMailReceivers = window.app.rewardcampaign.cc_mail_receivers
  const redirectToOnSuccess = window.app.rewardcampaign.redirectToOnSuccess
  const mailSubject = window.app.rewardcampaign.mail_subject

  function parseDealerNames ()
  {
    const dealerNamesString = window.app.rewardcampaign.dealer_names;
    return dealerNamesString ? dealerNamesString.split(',').map(name => name.trim()) : []
  }

  function pushToStepOneProducts (product) {
    stepOne.value.products.push(product)
  }

  function deleteStepOneProduct (id) {
    stepOne.value.products = stepOne.value.products.filter(productId => productId.id !== id)
  }

  function areAllProductsValid () {
    return stepOne.value.products.every(product => product.valid === true)
      && stepOne.value.products.every(product => product.id !== '')
      && stepOne.value.products.every(product => product.buyDate !== '')
      && stepOne.value.products.every(product => product.buyPrice !== '')
      && stepOne.value.products.every(product => product.invoiceNr !== '')
      && stepOne.value.products.every(product => product.dealer !== '')
      && stepOne.value.products.every(product => product.serialNumber !== '')
  }

  function getProductById (id) {
    return stepOne.value.products.find(product => product.id === id)
  }

  async function validateStepOneProductSerialNr (serialNr) {
    let serialValidateResponse = null

    try {
      const response = await serialNumberApiClient.get(window.app.rewardcampaign.api_serialnumber_validate_endpoint + serialNr)
      serialValidateResponse = response.data
    } catch (error) {
      // console.error('Error fetching data:', error)
    }

    if (serialValidateResponse && serialValidateResponse.state === 'VALID')
    {
      return serialValidateResponse.itemNo
    }

    return false
  }

  async function fetchStepOneProductIfValidSerialNr (itemNo, id) {
    let itemRepoResponse = null
    const index = stepOne.value.products.findIndex(product => product.id === id)

    if (index >= 0) {
      try {
        const params = {
          site: window.app.rewardcampaign.site
        }
        const response = await itemRepoApiClient.get(window.app.rewardcampaign.api_itemrepo_endpoint + window.app.rewardcampaign.siteLanguageTwoLetterIsoCode + '/' + itemNo, { params })
        itemRepoResponse = response.data
        stepOne.value.products[index].searchedProductHit.tpgName = itemRepoResponse?.tpgName
        stepOne.value.products[index].searchedProductHit.tpgCode = itemRepoResponse?.tpgCode
        stepOne.value.products[index].searchedProductHit.itemNo = itemRepoResponse?.itemNo
        stepOne.value.products[index].searchedProductHit.imageUrl = itemRepoResponse?.productImageData.imageUrl
      } catch (error) {
        // console.error('Error fetching data:', error)
      }
    }
  }

  function setStepOneUploadedFiles (files) {
    if (files && (files instanceof File || files instanceof FileList)) {
      const newFiles = Array.from(files)
      const existingFileNames = stepOne.value.files.map(file => file.name)

      // Filter out new files that already exist by name
      const filteredFiles = newFiles.filter(file => !existingFileNames.includes(file.name))

      stepOne.value.files = [...stepOne.value.files, ...filteredFiles]
    }
  }

  function removeFileByName (fileName) {
    if (stepOne.value.files) {
      stepOne.value.files = Array.from(stepOne.value.files).filter(file => file.name !== fileName)
    }
  }

  function parseArticleStringToArray (articleNrString) {
    let result = []

    if (articleNrString.includes(',')) {
      result = articleNrString.split(',').map(str => str.trim())
    } else {
      result.push(articleNrString.trim())
    }

    return result
  }

  function findFirstNestedObject(obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return { name: key, value: value }
      }
    }
    return null
  }

  function filterFirstThreeNonNullValues(obj) {
    let newObj = []
    let count = 0

    for (const [key, value] of Object.entries(obj)) {
      if (key && value !== null) {
        newObj[count] = value
        count++
        if (count >= 3) {
          break
        }
      }
    }

    return newObj
  }

  function sanitizeJSONString(str) {
    return str.replace(/\\n/g, '\\n')
      .replace(/\\r/g, '\\r')
      .replace(/\\t/g, '\\t')
      .replace(/\\f/g, '\\f')
      .replace(/\\b/g, '\\b')
      .replace(/\\"/g, '"')
  }

  async function initRewards () {
    const articleNrString = window.app.rewardcampaign.rewardarticlenumbers

    let manualRewardsArr = []
    const manualRewardInput = window.app.rewardcampaign.manualrewardinput

    if (manualRewardInput) {
      if (typeof manualRewardInput === 'string' || manualRewardInput instanceof String) {
        const sanitizedJson = sanitizeJSONString(manualRewardInput)
        manualRewardsArr = JSON.parse(sanitizedJson)
      } else if (manualRewardInput instanceof Array) {
        manualRewardsArr = manualRewardInput
      }

      manualRewardsArr.forEach(item => {
        item.selected = false
        item.isManualReward = true
      })
    }

    stepThree.value.manualRewards = manualRewardsArr

    let rewardItemIds = []

    if (articleNrString) {
      rewardItemIds = parseArticleStringToArray(articleNrString)
    }

    if (rewardItemIds.length > 0)
    {
      let validItemCounter = 0

      for (const itemNo of rewardItemIds) {
        let itemRepoResponse = null

        try {
          const params = {
            site: window.app.rewardcampaign.site
          }
          const response = await itemRepoApiClient.get(window.app.rewardcampaign.api_itemrepo_endpoint + window.app.rewardcampaign.siteLanguageTwoLetterIsoCode + '/' + itemNo, { params })

          itemRepoResponse = response.data
          const nestedFeatureObj = findFirstNestedObject(itemRepoResponse?.contentElements?.content?.product_features)

          stepThree.value.rewards[validItemCounter] = {
            tpgName: itemRepoResponse?.tpgName,
            tpgCode: itemRepoResponse?.tpgCode,
            itemNo: itemRepoResponse?.itemNo,
            slug: itemRepoResponse?.slug,
            imageUrl: itemRepoResponse?.productImageData?.imageUrl,
            features: filterFirstThreeNonNullValues(nestedFeatureObj.value),
            selected: false
          }

          validItemCounter++
        } catch (error) {
          // console.error('Error fetching data:', error)
        }
      }
    }
  }

  function isAnyRewardSelected () {
    return stepThree.value.rewards.some(reward => reward.selected) || stepThree.value.manualRewards.some(reward => reward.selected)
  }

  function findSelectedReward () {
    let selectedReward = stepThree.value.rewards.find(reward => reward.selected)

    if (!selectedReward) {
      return stepThree.value.manualRewards.find(reward => reward.selected)
    }

    return selectedReward
  }

  function goToNextStep() {
    if (activeStep.value < maxStep.value) {
      const validState = validateStoreOnStepChange(activeStep.value + 1)
      isErrorState.value = !validState

      if (validState)
      {
        activeStep.value++
      }
    }
  }

  function goToStep(value) {
    if (value >= minStep.value && value <= maxStep.value) {
      const validState = validateStoreOnStepChange(value)
      isErrorState.value = !validState

      if (validState)
      {
        activeStep.value = value
      }
    }
  }

  function validateBeforeSecondStep () {
    return areAllProductsValid() && stepOne.value.products.length > 0 && stepOne.value.files.length > 0
  }

  function validateBeforeThirdStep () {
    let valid = validateBeforeSecondStep()

    if (valid) {
      // validate second step
      valid = stepTwo.value.personalData.email !== '' && stepTwo.value.personalData.firstname !== ''
        && stepTwo.value.personalData.lastname !== '' && stepTwo.value.personalData.lastname !== ''
        && stepTwo.value.deliveryData.firstname !== '' && stepTwo.value.deliveryData.lastname !== ''
        && stepTwo.value.deliveryData.street !== '' && stepTwo.value.deliveryData.houseNo !== ''
        && stepTwo.value.deliveryData.zip !== '' && stepTwo.value.deliveryData.city !== ''

      // if valid goes to step 3 otherwise stays at 2
      return valid
    } else {
      goToStep(1)
      return false
    }
  }

  function validateBeforeFourthStep () {
    let valid = validateBeforeSecondStep()

    if (!valid) {
      goToStep(1)
      return false
    }

    valid = validateBeforeThirdStep()

    if (!valid)
    {
      goToStep(2)
      return false
    }

    if (!isAnyRewardSelected())
    {
      goToStep(3)
      return false
    }

    return true
  }

  function validateStoreOnStepChange (step) {
    if (step === 2) {
      return validateBeforeSecondStep()
    }

    if (step === 3) {
      return validateBeforeThirdStep()
    }

    if (step === 4) {
      return validateBeforeFourthStep()
    }

    return true
  }

  function handleSubmit (captchaSolution) {
    const isValid = validateBeforeFourthStep()

    if (isValid && stepFour.value.isConditionsOfParticipationChecked && stepFour.value.isDataPrivacyChecked) {
      let params = new URLSearchParams()
      params.append('solution', captchaSolution)

      if (Array.isArray(stepOne.value.products)) {
        stepOne.value.products.forEach((product, index) => {
          for (const key in product) {
            if (key !== 'id' && key !== 'valid' && Object.prototype.hasOwnProperty.call(product, key)) {
              // Check if the current value is an object
              if (typeof product[key] === 'object' && product[key] !== null) {
                // Iterate over the object properties
                for (const subKey in product[key]) {
                  if (Object.prototype.hasOwnProperty.call(product[key], subKey)) {
                    if (subKey !== 'tpgCode' && subKey !== 'imageUrl') {
                      params.append(`products[${index}][${subKey}]`, product[key][subKey])
                    }
                  }
                }
              } else {
                // If not an object, append the value as usual
                params.append(`products[${index}][${key}]`, product[key])
              }
            }
          }
        })
      }

      let formData = new FormData()
      const files = stepOne.value.files

      if (files instanceof Array) {
        files.forEach((file, index) => {
          if (file instanceof File) {
            formData.append(`files[${index}]`, file)
          }
        })
      } else if (files instanceof File) {
        formData.append('files[0]', files)
      } else {
        console.log('Unknown file input type:', files)
      }

      const stepTwoPersonalVal = stepTwo.value.personalData
      const stepTwoPersonalData = {
        email: stepTwoPersonalVal.email,
        firstname: stepTwoPersonalVal.firstname,
        lastname: stepTwoPersonalVal.lastname,
        phone: stepTwoPersonalVal.phone
      }
      params.append('personalData', JSON.stringify(stepTwoPersonalData))

      const stepTwoDeliveryVal = stepTwo.value.deliveryData
      const stepTwoDeliveryData = {
        company: stepTwoDeliveryVal.company,
        firstname: stepTwoDeliveryVal.firstname,
        lastname: stepTwoDeliveryVal.lastname,
        street: stepTwoDeliveryVal.street,
        houseNo: stepTwoDeliveryVal.houseNo,
        zip: stepTwoDeliveryVal.zip,
        city: stepTwoDeliveryVal.city
      }
      params.append('deliveryData', JSON.stringify(stepTwoDeliveryData))

      const selectedReward = findSelectedReward()
      params.append('selectedReward', JSON.stringify(selectedReward))

      params.append('subscribeToNewsletter', stepFour.value.isNewsletterChecked)
      params.append('mailToAddress', mailReceiver)
      params.append('ccMailAddresses', ccMailReceivers)
      params.append('mailSubject', mailSubject)

      postRewardCampaignData(params, formData)
        .then(response => {
          if (response.status === 200) {
            // redirect
            if (redirectToOnSuccess) {
              window.location.href = redirectToOnSuccess;
            }
          } else {
            alert(t('stepFour.submitError'))
          }
        })
        .catch(error => {
          console.log('error:', error)
        })
    }
  }

  return { activeStep, isErrorState, detailPageUrl, frcSitekey, privacyPageUrl, termsOfParticipationPageUrl, stepOne, stepTwo, stepThree, stepFour, goToNextStep, goToStep, pushToStepOneProducts, deleteStepOneProduct, setStepOneUploadedFiles, removeFileByName, validateStepOneProductSerialNr, fetchStepOneProductIfValidSerialNr, areAllProductsValid, getProductById, initRewards, findSelectedReward, handleSubmit }
})
